import React, { Fragment, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';

import PresentationContext from '../PresentationContext';
import TargetContext, { defaultTargets } from './TargetContext';
import { consts, internalUnits, mgdL, mmolL } from '../utils/consts';
import './Dashboard.css';

const defaultTicks = consts[internalUnits].ticks;
const defaultRange = [defaultTicks.max, defaultTicks.min];

function getReducer(formatGlucoseValue, units, ticks, contextTargets, setTargets) {
  function editStateReducer(state, action) {

    const { type, payload, event } = action;

    function validateGlucose(value) {
      // We could allow entry in the other format too here, by letting unit be undefined:
      // let mmolValue = +formatGlucoseValue({ value }, mgdL);
      // But let's avoid surprises here.
      let mmolValue = +formatGlucoseValue({ value, unit: units }, mgdL);
      switch (state.focusedTarget) {
        case 'top':
          mmolValue = Math.min(ticks.max, Math.max(state.storedTargets.bottom, mmolValue));
          break;
        case 'bottom':
          mmolValue = Math.min(state.storedTargets.top, Math.max(0, mmolValue));
          break;
        default:
      }
      return +formatGlucoseValue({ value: mmolValue, unit: mgdL }, units);
    }

    switch (type) {
      case 'edit':
        return {
          ...state,
          ...payload,
          targetsEditable: true,
          typing: false,
        };
      case 'add':
        let editedValue = `${
          state.typing ? state.editedValue : ''
        }${
          payload.editedValue
        }`;
        if (editedValue === '.') {
          editedValue = '0.';
        }
        return {
          ...state,
          editedValue,
          typing: true,
        };
      case 'increment':
      case 'decrement':
        let increment = (units === mmolL ? 0.1 : 1) * (type === 'increment' ? 1 : -1);
        if (event.shiftKey) {
          increment *= 10;
        }
        let incrementedValue = +(state.editedValue.length
          ? state.editedValue
          : formatGlucoseValue({
            value: state.storedTargets[state.focusedTarget],
            unit: mgdL,
          }, units)
        ) + increment;
        if (event.controlKey || event.metaKey) {
          incrementedValue = units === mmolL
          ? Math.round(incrementedValue)
          : (Math.round(incrementedValue / 10) * 10);
        }
        return {
          ...state,
          editedValue: incrementedValue,
          storedTargets: {
            ...state.storedTargets,
            [state.focusedTarget]: +formatGlucoseValue({
              value: validateGlucose(incrementedValue),
              unit: units,
            }, mgdL),
          },
          typing: false,
        };
      case 'move':
        let value = state.storedTargets[state.focusedTarget] - payload;
        switch (state.focusedTarget) {
          case 'top':
            value = Math.min(consts[mgdL].ticks.max, Math.max(state.storedTargets.bottom, value));
            break;
          case 'bottom':
            value = Math.min(state.storedTargets.top, Math.max(0, +value));
            break;
          default:
        }
        return {
          ...state,
          editedValue: '',
          storedTargets: {
            ...state.storedTargets,
            [state.focusedTarget]: value,
          },
        };
      case 'set':
        return {
          ...state,
          editedValue: '',
          storedTargets: {
            ...state.storedTargets,
            [state.focusedTarget]: validateGlucose(payload),
          },
        };
      case 'tabTarget':
        return {
          ...state,
          editedValue: '',
          focusedTarget: state.focusedTarget === 'top' ? 'bottom' : 'top',
          storedTargets: {
            ...state.storedTargets,
            [state.focusedTarget]: state.typing
            ? +formatGlucoseValue({ value: +state.editedValue, unit: units }, mgdL)
            : state.storedTargets[state.focusedTarget],
          },
        };
      case 'finalize':
        select('body').on('keydown.editTarget', null);
        return {
          ...state,
          editedValue: '',
          focusedTarget: undefined,
          storedTargets: {
            ...state.storedTargets,
            [state.focusedTarget]: state.typing
            ? +formatGlucoseValue({ value: validateGlucose(+state.editedValue), unit: units }, mgdL)
            : state.storedTargets[state.focusedTarget],
          },
        };
      case 'endEdit':
        return {
          ...state,
          editedValue: '',
          focusedTarget: undefined,
          isInt: true,
          targetsEditable: false,
        };
      case 'reset':
        return {
          ...state,
          editedValue: '',
          focusedTarget: undefined,
          isInt: true,
          storedTargets: defaultTargets,
          targetsEditable: false,
        };
      case 'replace':
        return {
          ...state,
          ...payload,
        };
      default:
        return state;
    }
  }

  return function editAndCommitTargetsState(state, action) {
    const newState = editStateReducer(state, action);
    if (action.type !== 'replace' && ((newState.storedTargets.top !== contextTargets.top) || (newState.storedTargets.bottom !== contextTargets.bottom))) {
      setTargets(newState.storedTargets);
    }
    return newState;
  }
}

function GlucoseYAxis({ ticks = defaultTicks, width = 721 }) {
  const { t } = useTranslation('dashboard');

  const { targets, setTargets } = useContext(TargetContext);
  const initialState = {
    editedValue: '',
    focusedTarget: undefined,
    isInt: true,
    typing: false,
    storedTargets: targets,
    targetsEditable: false,
  };

  const { formatGlucoseValue, formatGlucoseUnits, units, toggleUnits } = useContext(PresentationContext);
  const [ stateTargets, setStateTargets ] = useState(targets);

  useEffect(() => {
    setTargets(stateTargets);
  }, [setTargets, stateTargets]);

  const targetReducer = useMemo(() => getReducer(formatGlucoseValue, units, ticks, targets, setStateTargets),
  [formatGlucoseValue, units, ticks, targets, setStateTargets]);

  const domain = [ticks.max, ticks.min];
  const [state, dispatch] = useReducer(targetReducer, initialState);
  const scale = scaleLinear().domain(domain).range(defaultRange);
  const tickValues = Object.values(ticks);

  function clearSelection() {
    if (document.getSelection) {
      document.getSelection().removeAllRanges();
    }
  }

  useEffect(() => {
    if ((state.storedTargets.top !== targets.top) || (state.storedTargets.bottom !== targets.bottom)) {
      dispatch({ type: 'replace', payload: { storedTargets: { ...targets } }});
    }
  }, [state.storedTargets, targets]);

  function initKeyHandler(target) {
    select('body').on(`keydown.${target}`, function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        select('body').on(`keydown.${target}`, null);
        editTarget(target);
      }
    });
  }

  function releaseKeyHandler(target) {
    select('body').on(`keydown.${target}`, null);
  }

  function initPointerHandler(target, pointerEvent, useMouseEvents = false) {
    const initialY = pointerEvent.clientY;

    editTarget(target);
    let busy = false;
    let dragged = false;
    if (state.focusedTarget === target) {
      dispatch({ type: 'endEdit' });
      return;
    }
    const svg = select(pointerEvent.target.ownerSVGElement || '#details > svg');
    const scale = (svg.node().viewBox.baseVal.height || 410) / svg.node().clientHeight;
    let moved = 0;
    select('body')
      .style('touch-action', 'none')
      .on(`${useMouseEvents ? 'mouse' : 'pointer'}move.editTarget`, (event) => {
        dragged = true;
        const pointerEvent = event;
        if (!busy) {
          busy = true;
          const move = ((pointerEvent.clientY - initialY) * scale) - moved;
          window.setTimeout(() => {
            dispatch({
              type: 'move',
              payload: move,
              event,
            });
            moved += move;
            clearSelection();
            busy = false;
          }, 0);
        }
      })
      .on(`${useMouseEvents ? 'mouse' : 'pointer'}up.editTarget`, () => {
        if (dragged) {
          dispatch({ type: 'endEdit' });
        }
        select('body')
          .style('touch-action', 'auto')
          .on(`${useMouseEvents ? 'mouse' : 'pointer'}move.editTarget`, null)
          .on(`${useMouseEvents ? 'mouse' : 'pointer'}up.editTarget`, null);
        clearSelection();
      });
  }

  function editTarget(target) {
    dispatch({ type: 'edit', payload: { focusedTarget: target } });
    select('body').on('keydown.editTarget', function (event) {
      if (units === mmolL && state.isInt && (event.code === 'Period' || event.code === 'Comma')) {
        dispatch({
          type: 'add',
          payload: {
            editedValue: '.',
            isInt: false,
            typing: true,
          },
          event,
        });
      } else if (event.keyCode >= 48 && event.keyCode <= 57) {
        dispatch({
          type: 'add',
          payload: {
            editedValue: event.key,
            typing: true,
          },
        });
      } else if (event.code === 'ArrowUp') {
        event.preventDefault();
        dispatch({ type: 'increment', payload: units, event });
      } else if (event.code === 'ArrowDown') {
        event.preventDefault();
        dispatch({ type: 'decrement', payload: units, event });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        select('body').on('keydown.editTarget', null);
        dispatch({ type: 'finalize', payload: units, event });
        initKeyHandler(target);
      } else if (event.code === 'Tab') {
        if (state.focusedTarget) {
          event.preventDefault();
          dispatch({ type: 'tabTarget', payload: units, event });
        }
      } else if (event.code === 'Backspace') {
        event.preventDefault();
        dispatch({
          type: 'set',
          payload: { value: state.editedValue.substring(0, state.editedValue.length - 1) || ''},
          event,
        });
      }
    });
  }

  const targetRangeTextLength = 60;
  const targetRangeTextPadding = 3;
  const targetRangeLineRound = 5;
  const targetRangeVerticalLineLength = Math.max(
    ((state.storedTargets.top - state.storedTargets.bottom - targetRangeTextLength) / 2)
    - targetRangeTextPadding - targetRangeLineRound,
    0
  );
  const targetTextPositionY = targetRangeVerticalLineLength
  ? targetRangeVerticalLineLength + targetRangeLineRound + targetRangeTextPadding
  : (state.storedTargets.top - state.storedTargets.bottom - targetRangeTextLength) / 2;
  const targetRangeTranslateX = (((state.storedTargets.top - state.storedTargets.bottom -
    ((targetRangeVerticalLineLength + targetRangeLineRound + targetRangeTextPadding) * 2))
    >= targetRangeTextLength))
    ? -35
    : -45;

  const targetRangePresent = (state.storedTargets.top <= tickValues[0])
    && (state.storedTargets.bottom >= tickValues[tickValues.length - 1]);
  const targetsEdited = (state.storedTargets.bottom !== defaultTargets.bottom)
    || (state.storedTargets.top !== defaultTargets.top);

  return (
    <g className="y-axis">
      {
        tickValues.map(tick => (
          (!targetRangePresent || (tick !== ticks.high && tick !== ticks.low))
            ? (
              <g key={tick} transform={`translate(0,${scale(tickValues[0] - tick)})`}>
                <line vectorEffect="non-scaling-stroke" x2={width} />
                <text dx="-6" dy="0.35em">{formatGlucoseValue({ value: tick, unit: mgdL })}</text>
              </g>
            )
            : (
              <Fragment key={tick} />
            )
        ))
      }
      {targetRangePresent && (
        <g
          className={`targets${targetsEdited ? ' edited' : ''}`}
          transform={`translate(0,${defaultTicks.max - state.storedTargets.top})`}
        >
          <path
            className="targetBG"
            d={`M -38 ${
              targetRangeVerticalLineLength + targetRangeLineRound
              } v ${
              -targetRangeVerticalLineLength
              } a ${
              targetRangeLineRound
              } ${
              targetRangeLineRound
              } 0 0 1 ${
              targetRangeLineRound
              } ${
              -targetRangeLineRound
              } h ${
                width + 38
              } v ${
              state.storedTargets.top - state.storedTargets.bottom
              } h ${
                -width - 38
              } a ${
              targetRangeLineRound
              } ${
              targetRangeLineRound
              } 0 0 1 ${
              -targetRangeLineRound
              } ${
              -targetRangeLineRound
              } v ${
              -targetRangeVerticalLineLength
              } v ${
              state.storedTargets.bottom - state.storedTargets.top
              }`}
            shapeRendering="auto"
          />
          <path
            className="target"
            d={`M -38 ${
              targetRangeVerticalLineLength + targetRangeLineRound
              } v ${
              -targetRangeVerticalLineLength
              } a ${
              targetRangeLineRound
              } ${
              targetRangeLineRound
              } 0 0 1 ${
              targetRangeLineRound
              } ${
              -targetRangeLineRound
              } h ${
                width + 33
              } m 0 ${
              state.storedTargets.top - state.storedTargets.bottom
              } h ${
                -width - 33
              } a ${
              targetRangeLineRound
              } ${
              targetRangeLineRound
              } 0 0 1 ${
              -targetRangeLineRound
              } ${
              -targetRangeLineRound
              } v ${
              -targetRangeVerticalLineLength
              } m 0 ${
              state.storedTargets.bottom - state.storedTargets.top
              }`}
            shapeRendering="auto"
          />
          <g
            className={`adjustable${state.targetsEditable ? ` editable${state.focusedTarget === 'top' ? ' focused' : ''}` : ''}`}
            tabIndex="0"
            onFocus={() => initKeyHandler('top')}
            onBlur={() => releaseKeyHandler('top')}
            onPointerDown={(e) => initPointerHandler('top', e)}
            onMouseDown={window.PointerEvent ? undefined : (e) => initPointerHandler('top', e, true)}
          >
            <rect x="-28" y={targetRangeLineRound} rx="2" width="26" height="12" />
            <text
              dx="-6"
              dy="15"
            >
              {((state.focusedTarget === 'top') && (state.editedValue.length))
                ? state.editedValue
                : formatGlucoseValue({ value: state.storedTargets.top, unit: mgdL })
              }
            </text>
          </g>
          <g
            className={`adjustable${state.targetsEditable ? ` editable${state.focusedTarget === 'bottom' ? ' focused' : ''}` : ''}`}
            tabIndex="0"
            onFocus={() => initKeyHandler('bottom')}
            onBlur={() => releaseKeyHandler('bottom')}
            onPointerDown={(e) => initPointerHandler('bottom', e)}
            onMouseDown={window.PointerEvent ? undefined : (e) => initPointerHandler('bottom', e, true)}
            transform={`translate(0,${state.storedTargets.top - state.storedTargets.bottom})`}
          >
            <rect x="-28" y={-targetRangeLineRound - 12} rx="2" width="26" height="12" />
            <text
              dx="-6"
              dy="-7"
            >
              {((state.focusedTarget === 'bottom') && (state.editedValue.length))
                ? state.editedValue
                : formatGlucoseValue({ value: state.storedTargets.bottom, unit: mgdL })
              }
            </text>

          </g>
          <g
            className="interactive"
            textAnchor="middle"
            transform={`translate(${
                targetRangeTranslateX
              },${
                targetTextPositionY
              }) rotate(-90)`
            }
            onClick={() => {
              if (!state.targetsEditable) {
                editTarget('top');
              } else {
                dispatch({ type: 'endEdit' });
              }
            }}
            onDoubleClick={() => {
              dispatch({ type: 'reset' });
            }}
          >
            <rect x={-targetRangeTextLength - 2} y="-12" width={targetRangeTextLength + 4} rx="2" height="16" />
            <text textLength={targetRangeTextLength} lengthAdjust="spacingAndGlyphs">
              {t('glucoseStatsRow.tir.target')}
            </text>
          </g>
        </g>
      )
      }
      <text className="interactive legend" transform={`translate(${targetRangeTranslateX -19},125) rotate(-90)`} onClick={toggleUnits}>{formatGlucoseUnits(units)}</text>
    </g>
  );
}

export default GlucoseYAxis;
